import {view, tokens, Divider, css} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {ThemeProvider, LinkTheme} from '@stripe-internal/conversations-ui';
import {defineMessages, FormattedMessage} from 'react-intl';

export type ValueProps = {
  account_email: string;
  account_avatar: string;
  account_name: string;
  creation_date: string;
  created_through: string;
  active_subscriptions: string;
};

const messages = defineMessages({
  accountTitle: {
    id: 'mse_inline_data.link_account_details.account_title',
    description: 'Account label',
    defaultMessage: 'Account',
  },
  creationDateTitle: {
    id: 'mse_inline_data.link_account_details.creation_date_title',
    description: 'Creation date label',
    defaultMessage: 'Creation date',
  },
  createdThroughTitle: {
    id: 'mse_inline_data.link_account_details.created_through_title',
    description: 'Created through label',
    defaultMessage: 'Created through',
  },
  activeSubscriptionsTitle: {
    id: 'mse_inline_data.link_account_details.active_subscriptions_title',
    description: 'Active subscriptions label',
    defaultMessage: 'Active subscriptions',
  },
});

const getFirstInitial = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length === 0) return '';
  return parts[0][0]?.toUpperCase() || '';
};

const LinkAccountDetailsCard = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const accountPropsValue = datum.value as ValueProps;
  const {
    account_email: accountEmail,
    account_name: accountName,
    account_avatar: accountAvatar,
    creation_date: creationDate,
    created_through: createdThrough,
    active_subscriptions: activeSubscriptions,
  } = accountPropsValue;

  const analytics = useAnalytics();
  analytics.viewed(`viewed_link_link_account_details`);

  const initials = getFirstInitial(accountName);

  const cardStyles = css({
    backgroundColor: tokens.color.background.surface,
    border: '1px solid',
    borderColor: tokens.color.border,
    borderRadius: '16px',
    padding: '16px',
    gap: '12px',
    width: 'fill',
    ':hover': {
      boxShadow:
        '0px 7px 14px rgba(23, 23, 23, 0.08), 0px 3px 6px rgba(23, 23, 23, 0.12)',
    },
  });
  const titleStyles = css({
    fontWeight: '500',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.006em',
  });
  const subTitleDataStyles = css({
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.006em',
  });

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <view.div uses={[cardStyles]}>
        <view.div css={{stack: 'y', gap: '12px'}}>
          <view.div css={{stack: 'x', gap: '6px'}}>
            <view.span css={{textColor: tokens.color.text.onAction.secondary}}>
              <FormattedMessage {...messages.accountTitle} />
            </view.span>
            <view.span css={{height: '10px', stack: 'x'}}>
              <Divider />
            </view.span>
            <view.span css={{textColor: tokens.color.text.subdued}}>
              {accountEmail}
            </view.span>
          </view.div>

          <view.div css={{stack: 'x', gap: '8px', alignY: 'center'}}>
            {accountAvatar ? (
              <view.img
                data-testid="account-avatar"
                src={accountAvatar}
                alt={accountName}
                css={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <view.div
                data-testid="account-avatar"
                css={{
                  width: '32px',
                  height: '32px',
                  gap: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  backgroundColor: LinkTheme.brandColor,
                  padding: '16px',
                  color: LinkTheme.primaryActionColor,
                  fontSize: '16px',
                  lineHeight: '24px',
                  fontWeight: '570',
                }}
              >
                {initials}
              </view.div>
            )}

            <view.div
              css={{
                fontSize: '24px',
                lineHeight: '32px',
                fontWeight: '600',
                letterSpacing: '-0.02em',
                color: LinkTheme.primaryActionColor,
              }}
              data-testid="account-name"
            >
              {accountName}
            </view.div>
          </view.div>

          <view.div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              color: LinkTheme.primaryActionColor,
            }}
          >
            <view.div css={{flex: '1 1 auto', stack: 'y', gap: 'xxsmall'}}>
              <view.div uses={[titleStyles]}>
                <FormattedMessage {...messages.creationDateTitle} />
              </view.div>
              <view.div uses={[subTitleDataStyles]} data-testid="creation-date">
                {creationDate}
              </view.div>
            </view.div>

            <view.div css={{flex: '1 1 auto', stack: 'y', gap: 'xxsmall'}}>
              <view.div uses={[titleStyles]}>
                <FormattedMessage {...messages.createdThroughTitle} />
              </view.div>
              <view.div
                uses={[subTitleDataStyles]}
                data-testid="created-through"
              >
                {createdThrough}
              </view.div>
            </view.div>
          </view.div>

          <view.div
            css={{
              stack: 'y',
              gap: '16px',
              color: LinkTheme.primaryActionColor,
            }}
          >
            <view.div css={{stack: 'y', gap: '2x'}}>
              <view.div uses={[titleStyles]}>
                <FormattedMessage {...messages.activeSubscriptionsTitle} />
              </view.div>
              <view.div
                uses={[subTitleDataStyles]}
                data-testid="active-subscriptions"
              >
                {activeSubscriptions}
              </view.div>
            </view.div>
          </view.div>
        </view.div>
      </view.div>
    </view.div>
  );
};

export const LinkAccountDetails = (props: InlineDatumReactComponentProps) => {
  return (
    <ThemeProvider value={LinkTheme}>
      <LinkAccountDetailsCard props={props} />
    </ThemeProvider>
  );
};
